import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import { getMeasurementLabelFromId } from 'Util/_HumanWritten/MeasurementUtils';

export interface ICargoTypeEntityAttributes extends IModelAttributes {
	cargoMake: string;
	cargoModel: string;
	disabled: boolean;
	isRestricted: boolean;

	minimumWidthId?: string;
	minimumWidth?: Models.WidthEntity | Models.IWidthEntityAttributes;

	minimumLengthId: string;
	minimumLength: Models.MeasurementEntity | Models.IMeasurementEntityAttributes;
	minimumWeightId: string;
	minimumWeight: Models.MeasurementEntity | Models.IMeasurementEntityAttributes;
	exactWeight?: number | null;
	exactWeightUnit?: string | null;
	cargos: Array<Models.CargoEntity | Models.ICargoEntityAttributes>;
	waitlistItems: Array<Models.WaitlistItemEntity | Models.IWaitlistItemEntityAttributes>;
	cargoCharacteristics?: Array<Models.CargoTypeCargoCharacteristic | Models.ICargoTypeCargoCharacteristicAttributes>;
}
@entity('CargoTypeEntity', 'Vehicle Type')
export default class CargoTypeEntity extends Model implements ICargoTypeEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<CargoTypeEntity, string>()
	public cargoMake: string;

	@observable
	@attribute<CargoTypeEntity, string>()
	public cargoModel: string;

	/**
	 * Whether or not the vehicle type is disabled in the application.
	 */
	@observable
	@attribute<CargoTypeEntity, boolean>()
	public disabled: boolean;

	/**
	 * Whether or not the item is for only staff to see.
	 */
	@observable
	@attribute<CargoTypeEntity, boolean>()
	public isRestricted: boolean;

	@observable
	@attribute()
	public minimumWidthId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public minimumWidth: Models.WidthEntity;

	/**
	 * The minimum height required for this cargo item.
	 */
	/**
	 * The minimum length required for this cargo item.
	 */
	@observable
	@attribute()
	public minimumLengthId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public minimumLength: Models.MeasurementEntity;

	/**
	 * The minimum weight required for this cargo item.
	 */
	@observable
	@attribute()
	public minimumWeightId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public minimumWeight: Models.MeasurementEntity;

	@observable
	@attribute<CargoTypeEntity, number | null>()
	public exactWeight: number | null;

	@observable
	@attribute<CargoTypeEntity, string | null>()
	public exactWeightUnit: string | null;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public cargos: Models.CargoEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public waitlistItems: Models.WaitlistItemEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public cargoCharacteristics: Models.CargoTypeCargoCharacteristic[] = [];

	constructor(attributes?: Partial<ICargoTypeEntityAttributes>) {
		super(attributes);

		this.cargos = [];
		this.waitlistItems = [];
		this.cargoCharacteristics = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ICargoTypeEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.cargoMake !== undefined) {
				this.cargoMake = attributes.cargoMake;
			}
			if (attributes.cargoModel !== undefined) {
				this.cargoModel = attributes.cargoModel;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (attributes.isRestricted !== undefined) {
				this.isRestricted = attributes.isRestricted;
			}
			if (attributes.minimumWidthId !== undefined) {
				this.minimumWidthId = attributes.minimumWidthId;
			}
			if (attributes.minimumWidth !== undefined) {
				if (attributes.minimumWidth === null) {
					this.minimumWidth = attributes.minimumWidth;
				} else if (attributes.minimumWidth instanceof Models.WidthEntity) {
					this.minimumWidth = attributes.minimumWidth;
					this.minimumWidthId = attributes.minimumWidth.id;
				} else {
					this.minimumWidth = new Models.WidthEntity(attributes.minimumWidth);
					this.minimumWidthId = this.minimumWidth.id;
				}
			}
			if (attributes.minimumLengthId !== undefined) {
				this.minimumLengthId = attributes.minimumLengthId;
			}
			if (attributes.minimumLength !== undefined) {
				if (attributes.minimumLength === null) {
					this.minimumLength = attributes.minimumLength;
				} else if (attributes.minimumLength instanceof Models.MeasurementEntity) {
					this.minimumLength = attributes.minimumLength;
					this.minimumLengthId = attributes.minimumLength.id;
				} else {
					this.minimumLength = new Models.MeasurementEntity(attributes.minimumLength);
					this.minimumLengthId = this.minimumLength.id;
				}
			}
			if (attributes.minimumWeightId !== undefined) {
				this.minimumWeightId = attributes.minimumWeightId;
			}
			if (attributes.minimumWeight !== undefined) {
				if (attributes.minimumWeight === null) {
					this.minimumWeight = attributes.minimumWeight;
				} else if (attributes.minimumWeight instanceof Models.MeasurementEntity) {
					this.minimumWeight = attributes.minimumWeight;
					this.minimumWeightId = attributes.minimumWeight.id;
				} else {
					this.minimumWeight = new Models.MeasurementEntity(attributes.minimumWeight);
					this.minimumWeightId = this.minimumWeight.id;
				}
			}
			if (attributes.exactWeight !== undefined) {
				this.exactWeight = attributes.exactWeight;
			}
			if (attributes.exactWeightUnit !== undefined) {
				this.exactWeightUnit = attributes.exactWeightUnit;
			}
			if (attributes.cargos !== undefined && Array.isArray(attributes.cargos)) {
				if (!this.cargos) {
					this.cargos = [];
				}
				for (const model of attributes.cargos) {
					if (model instanceof Models.CargoEntity) {
						this.cargos.push(model);
					} else {
						this.cargos.push(new Models.CargoEntity(model));
					}
				}
			}
			if (attributes.waitlistItems !== undefined && Array.isArray(attributes.waitlistItems)) {
				if (!this.waitlistItems) {
					this.waitlistItems = [];
				}
				for (const model of attributes.waitlistItems) {
					if (model instanceof Models.WaitlistItemEntity) {
						this.waitlistItems.push(model);
					} else {
						this.waitlistItems.push(new Models.WaitlistItemEntity(model));
					}
				}
			}
			if (attributes.cargoCharacteristics !== undefined && Array.isArray(attributes.cargoCharacteristics)) {
				if (!this.cargoCharacteristics) {
					this.cargoCharacteristics = [];
				}
				for (const model of attributes.cargoCharacteristics) {
					if (model instanceof Models.CargoTypeCargoCharacteristic) {
						this.cargoCharacteristics.push(model);
					} else {
						this.cargoCharacteristics.push(new Models.CargoTypeCargoCharacteristic(model));
					}
				}
			}
		}
	}

	/**
	 * Make Model
	 * Example: `Toyota Hilux` or `Toyota Hilx (Up to 5m)`
	 */
	public getFormattedVehicleMakeModel(measurementId?: string) {
		let result = `${this?.cargoMake} ${this?.cargoModel}`;
		if (measurementId) {
			result += this.getLengthLabelInBrackets(measurementId);
		}
		return result;
	}

	/**
	 * Example: `(Up to 5m)`
	 */
	public getLengthLabelInBrackets(measurementId: string) {
		return ` (${getMeasurementLabelFromId(measurementId)})`;
	}
}
